<template>
  <FormElement class="text-input-container" v-bind="$props">
    <label :class="classes">
      <input
        v-if="mask"
        ref="inputfield"
        v-model="inputValue"
        v-mask="mask"
        v-pattern="pattern"
        v-autofocus="shouldFocus"
        :autocomplete="autocomplete"
        :class="{ 'input': true, 'has-bar': hasBar }"
        :name="name"
        :disabled="disabled"
        :data-test="testName"
        :maxlength="maxLength"
        :readonly="readonly"
        v-bind="$attrs"
        :type="type"
        @input="onInput"
        @change="onChange"
        @focus="onFocus"
        @keyup="onKeyup"
        @keydown="onKeydown"
        @blur="onBlur"
        @transitionstart="onTransitionStart"
      />
      <input
        v-else
        ref="inputfield"
        v-model="inputValue"
        v-pattern="pattern"
        v-autofocus="shouldFocus"
        :autocomplete="autocomplete"
        :class="{ 'input': true, 'has-bar': hasBar }"
        :name="name"
        :disabled="disabled"
        :data-test="testName"
        :maxlength="maxLength"
        :readonly="readonly"
        v-bind="$attrs"
        :type="type"
        @input="onInput"
        @change="onChange"
        @focus="onFocus"
        @keyup="onKeyup"
        @keydown="onKeydown"
        @blur="onBlur"
        @transitionstart="onTransitionStart"
      />

      <div class="label">
        <span class="title">{{ localizedTitle }}</span>
        <span class="placeholder">{{ placeholder }}</span>
        <span v-if="hasBar" class="bar" />
      </div>
    </label>
    <slot name="after" />
  </FormElement>
</template>

<script>
import { mask } from '@titou10/v-mask'
import { isEmpty } from 'lodash'

import focusDirective from '../../directives/focus'
import pattern from '../../directives/pattern'
import FormElement from '../FormElement'

export default {
  name: 'TextInput',

  components: {
    FormElement,
  },

  directives: {
    pattern,
    mask,
    autofocus: focusDirective,
  },

  props: {
    value: {
      type: [String, Number],
      default: '',
    },

    mask: {
      type: [String, Array],
      default: '',
    },

    errors: {
      type: Object,
      default: () => {
        return {}
      },
    },

    autofocus: {
      type: Boolean,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    lazy: {
      type: Boolean,
      default: false,
    },

    titleLabel: {
      type: String,
      required: true,
    },

    name: {
      type: String,
      default: '',
    },

    placeholder: {
      type: String,
      default: '',
    },

    required: {
      type: Boolean,
      default: false,
    },
    isTouched: {
      type: Boolean,
      default: false,
    },
    negative: {
      type: Boolean,
      default: false,
    },
    darkBackground: {
      type: Boolean,
      default: false,
    },

    maxLength: {
      type: Number,
      default: 1000,
    },

    pattern: {
      type: String,
      default: '',
    },

    type: {
      type: String,
      default: 'text',
    },

    /**
     * Indents input content and label by a number of pixels
     * @example used essentially by the phone input who needs to indent the input content to display the country selector
     */
    indentContent: {
      type: Number,
      default: 0,
    },

    fixedPlaceholder: {
      type: Boolean,
      default: false,
    },

    autocomplete: {
      type: String,
      default: 'do-not-auto-complete',
    },

    hasBar: {
      type: Boolean,
      default: true,
    },

    onAutoFill: {
      type: Function,
      default: undefined,
    },

    readonly: {
      type: Boolean,
      default: false,
    },

    tooltipContent: {
      type: String,
      default: '',
    },

    tooltipLabel: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      isMobile: /Android|Mobi|iPad|iPhone/.test(navigator.userAgent),
      inputValue: this.value,
      isEdge: window.navigator.userAgent.includes(' Edge/'),
    }
  },

  computed: {
    /**
     * autofocus is disabled completely on mobile!
     * @return {Boolean}
     */
    shouldFocus() {
      return this.autofocus && !this.isMobile
    },

    localizedTitle() {
      return `${this.titleLabel}${this.required ? ' *' : ''}`
    },

    classes() {
      return {
        'text-input': true,
        'disabled': this.disabled,
        'negative': this.negative,
        'dark-background': this.darkBackground,
        'touched': !!this.inputValue || this.isTouched,
        'invalid': !isEmpty(this.errors),
        'readonly': this.readonly,
      }
    },

    testName() {
      return this.name ? this.name.split('[')[0] : ''
    },
  },

  watch: {
    value(value) {
      this.inputValue = value
    },
    indentContent: {
      handler: function (value) {
        this.$nextTick(() => {
          if (value > 0) {
            this.$el.getElementsByClassName('title')[0].style.paddingLeft =
              value + 'px'
            this.$el.getElementsByClassName('input')[0].style.paddingLeft =
              value + 'px'
            this.$el.getElementsByClassName(
              'placeholder',
            )[0].style.paddingLeft = value + 'px'
          } else {
            delete this.$el.getElementsByClassName('title')[0].style.paddingLeft
            delete this.$el.getElementsByClassName('input')[0].style.paddingLeft
            delete this.$el.getElementsByClassName('placeholder')[0].style
              .paddingLeft
          }
        })
      },
      immediate: true,
    },
  },

  methods: {
    isEmpty: isEmpty,

    // provides a callback when Webkit based browsers autofill an input, since this will not trigger a change event */
    // similar to this solution: https://rojas.io/how-to-detect-chrome-autofill-with-vue-js/
    onTransitionStart(e) {
      if (this.onAutoFill) {
        this.onAutoFill()
      }
    },

    /**
     * selects the content of this component's input field
     */
    select() {
      this.$refs.inputfield.select()
    },

    focus() {
      this.$refs.inputfield.focus()
    },

    onInput(event) {
      if (!this.lazy) {
        this.$emit('input', event.target.value)
      }
    },

    onChange(event) {
      if (this.lazy) {
        this.$emit('input', event.target.value)
      } else {
        this.$emit('change', event.target.value)
      }
    },

    onFocus(event) {
      this.$emit('focus', event.target.value)
    },

    onKeyup(event) {
      if (this.fixedPlaceholder) {
        const inputLength = event.target.value.length

        const isNumber = !isNaN(event.key)
        const isControlKey = [8, 46].indexOf(event.keyCode) !== -1

        // if the input is a number or delete/backspace, then add the placeholder - inputLength as value
        if (isNumber || isControlKey) {
          event.target.value = `${event.target.value}${this.placeholder.substr(
            inputLength,
          )}`
          event.target.selectionStart = inputLength
          event.target.selectionEnd = inputLength

          // move the selection one back, when a dot is reached
          if (
            isControlKey &&
            event.target.value.substr(inputLength - 1, 1) === '.'
          ) {
            event.target.selectionStart = inputLength - 1
            event.target.selectionEnd = inputLength - 1
          }
        }
      }

      this.$emit('keyup', event)
    },

    onKeydown(event) {
      this.$emit('keydown', event)
    },

    onBlur(event) {
      if (this.isEdge && this.lazy) {
        this.$emit('input', event.target.value)
      }
      this.$emit('blur', event.target.value)
    },

    reset() {
      this.inputValue = ''
      this.$emit('input', '')
    },
  },
}
</script>

<style scoped>
@value fieldIconSize: 25px;

.text-input-container {
  position: relative;
  display: flex;
  flex-direction: column;

  .text-input {
    display: inline-block;
    width: 100%;
    position: relative;
    cursor: text;

    &.readonly {
      input {
        user-select: none;
        cursor: default;
        opacity: 0.5;
      }

      .label {
        .title {
          top: 0;
          font-size: 12px;
        }
      }
    }

    &.disabled {
      & .input,
      & .title {
        color: var(--c-secondary-neutral-2) !important;
        cursor: no-drop;
      }
    }

    &.touched {
      .label {
        .title {
          top: 0;
          font-size: 12px;
        }

        .placeholder {
          display: none;
        }
      }
    }

    &.invalid {
      .label {
        .title {
          color: var(--c-secondary-color-1);
        }

        .bar {
          background-color: var(--c-secondary-color-1);
        }
      }

      input {
        border-bottom-color: var(--c-secondary-color-1);
      }
    }

    &.negative {
      &:not(.invalid) {
        .label {
          .title {
            color: var(--c-secondary-neutral-3);
          }

          .placeholder {
            color: var(--c-secondary-neutral-3);
          }

          .bar {
            background-color: var(--c-primary-neutral-3);
          }
        }

        .input {
          border-bottom-color: var(--c-primary-neutral-3);
        }
      }

      .input {
        color: var(--c-primary-neutral-3);
      }
    }

    &.dark-background {
      .input {
        background-color: var(--c-primary-neutral-3);
      }
    }

    .input {
      position: relative;
      width: 100%;
      z-index: 1;
      margin-top: 15px;
      font-size: 16px;
      line-height: 1.15;
      padding-left: 5px;
      border: none;
      background: none;
      outline: none;
      height: 40px;
      color: var(--c-primary-neutral-1);
      border-radius: 0;
      transform: translate3d(0, 0, 0);

      /* adding a no-effect transition to detect autofill in Webkit based browsers */

      &:-webkit-autofill {
        transition: background-color 0.1s ease-in-out 0s;
      }

      &.has-bar {
        border-bottom: 1px solid var(--c-primary-neutral-1);
      }

      @media (--md) {
        font-size: 18px;
      }

      &:invalid {
        box-shadow: none !important;
      }

      &:focus:not([readonly]) {
        outline: none;

        & ~ .label {
          .bar {
            width: 100%;
          }

          .title {
            top: 0;
            font-size: 12px;
          }

          .placeholder {
            opacity: 0;
            top: 25px;

            @media (--md) {
              opacity: 1;
            }
          }
        }
      }
    }

    .label {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;

      .title {
        position: absolute;
        top: 25px;
        line-height: 1.15;
        color: var(--c-primary-neutral-1);
        transition: all 0.25s ease;
      }

      .placeholder {
        position: absolute;
        top: 50px;
        left: 5px;
        font-size: 15px;
        color: var(--c-secondary-neutral-2);
        opacity: 0;
        transition: all 0.25s ease;
        height: 20px;
        line-height: 20px;
        pointer-events: none;

        @media (--md) {
          font-size: 18px;
        }
      }

      .bar {
        position: absolute;
        height: 3px;
        width: 0;
        bottom: 10px;
        background-color: var(--c-secondary-neutral-1);
        transition: width 0.3s ease;
      }
    }
  }
}
</style>
